import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Layout, Typography } from 'antd';
import Auth, { CognitoUser } from '@aws-amplify/auth';
import { Switch, Route, useHistory, useLocation, Link } from 'react-router-dom';

import { SignInForm } from './SignInForm';
import { NewPasswordForm } from './NewPasswordForm';
import { ForgotPasswordRequestForm } from './ForgotPasswordRequestForm';
import { ForgotPasswordFinishForm } from './ForgotPasswordFinishForm';
import { PageHeader } from '../../components/layout/PageHeader';
import LOGO from '../../images/dokapi-logo-dark-green-fern.svg';
import ILLUSTRATION from '../../images/DataBase_Files.H03.png';
import { useAuthenticationContext } from './AuthenticationContext';
import { User } from '../../types/users';

const { Sider } = Layout;
const { Title, Text } = Typography;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const SignIn = () => {
  const history = useHistory();
  const query = useQuery();
  const [signInResult, setSignInResult] = useState<null | (CognitoUser & User)>(null);
  const usernameRef = useRef(null);

  const { setLoggedInUser } = useAuthenticationContext();

  const onSignInSubmit = useCallback(
    async ({ username, password }, actions) => {
      await Auth.signIn(username, password)
        .then(result => {
          setSignInResult(result);
          if (result.challengeName === 'NEW_PASSWORD_REQUIRED') return history.push('/new-password-required');
        })
        .catch(error => {
          if (error.code === 'PasswordResetRequiredException') return history.push('/forgot-password');
          actions.setStatus('Incorrect username or password');
        });
      // try {
      //   const result = await Auth.signIn(username, password);
      //   debugger;
      //   setSignInResult(result);
      //   if (result.challengeName === 'NEW_PASSWORD_REQUIRED') return history.push('/new-password-required');
      // } catch (e) {
      //   if (e.code === 'PasswordResetRequiredException') return history.push('/forgot-password');
      //   actions.setStatus('Incorrect username or password');
      // }
    },
    [setSignInResult, history]
  );

  const onNewPasswordSubmit = useCallback(
    async ({ password }) => {
      const result = await Auth.completeNewPassword(signInResult, password, undefined);
      setSignInResult({ ...result });
    },
    [signInResult]
  );

  const onForgotPasswordRequestSubmit = useCallback(
    async values => {
      await Auth.forgotPassword(values.username);
      usernameRef.current = values.username;
      history.push(`/forgot-password-finish?username=${values.username}`);
    },
    [history, usernameRef]
  );

  const onForgotPasswordFinishSubmit = useCallback(
    async (values, actions) => {
      const username = query.get('username');
      if (!username) return;
      try {
        await Auth.forgotPasswordSubmit(username, values.code, values.password);
        usernameRef.current = null;
        history.push('/');
      } catch (e) {
        if ((e as any).code === 'LimitExceededException')
          return actions.setStatus('Too many invalid attempts. Please try again later.');

        actions.setStatus('Incorrect verification code.');
      }
    },
    [history, query]
  );

  useEffect(() => {
    if (signInResult && !signInResult.challengeName) setLoggedInUser(signInResult);
  }, [setLoggedInUser, signInResult]);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', flexGrow: 1, height: '100vh' }}>
      <Sider
        width={300}
        style={{
          padding: '0 32px',
          backgroundColor: '#FFFFFF',
          width: '100%',
          borderRight: '1px solid #DCDCDC',
        }}>
        <div style={{ padding: '45px 0' }}>
          <img src={LOGO} alt="Dokapi" style={{ width: '88px' }} />
        </div>

        <Switch>
          <Route path="/new-password-required">
            <div style={{ padding: '100px 0' }}>
              <PageHeader header="New Password" />
              <Text type="secondary" style={{ fontSize: 18 }}>
                Please enter a new password before logging in.
              </Text>
              <NewPasswordForm onSubmit={onNewPasswordSubmit} />
            </div>
          </Route>
          <Route path="/forgot-password">
            <div style={{ padding: '100px 0' }}>
              <PageHeader header="Forgot Password" />
              <Text type="secondary" style={{ fontSize: 18 }}>
                Please enter the email-address linked to your account.
              </Text>
              <ForgotPasswordRequestForm onSubmit={onForgotPasswordRequestSubmit} />
              <Link to="/">Go back</Link>
            </div>
          </Route>
          <Route path="/forgot-password-finish">
            <div style={{ padding: '100px 0' }}>
              <PageHeader header="Forgot Password" />
              <Text type="secondary" style={{ fontSize: 18 }}>
                Enter a new password and the code from the email you received.
              </Text>
              <ForgotPasswordFinishForm onSubmit={onForgotPasswordFinishSubmit} />
            </div>
          </Route>
          <Route path="/">
            <div style={{ padding: '100px 0' }}>
              <PageHeader header="Log in" />
              <Text type="secondary" style={{ fontSize: 18 }}>
                Welcome to the Dokapi Recognition demo.
              </Text>
              <SignInForm onSubmit={onSignInSubmit} />
              <Link to="/forgot-password">Forgot password</Link>
            </div>
          </Route>
        </Switch>
      </Sider>
      <div style={{ display: 'flex', flex: 1, flexDirection: 'column', position: 'relative' }}>
        <div
          style={{
            flex: 1,
            backgroundImage: `url(${require('../../images/6793883d4f521e8b9bfa7d2a_stripes-hor-on-dark.jpg')})`,
          }}
        />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}>
          <div style={{ margin: '80px 180px 0 180px', display: 'flex' }}>
            <Title style={{ color: 'white', fontSize: 48 }}>
              Dokapi, <span className="text-toolbox-secondary">dé slimme documentverwerker,</span> cloud based en AI
              empowered.
            </Title>
          </div>
          <div
            style={{
              display: 'flex',
              flex: 1,
              margin: 80,
              marginTop: 70,
              backgroundPosition: 'center',
              backgroundImage: `url(${ILLUSTRATION})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
            }}></div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
